
import './shared/scss/style.scss'
import { routes } from './Routes';
import { ConfigProvider } from 'antd';
import { antdThemeTokenConfig } from './antdConfig';
import 'dayjs/locale/zh-cn';
import { useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { useGetPermissionsQuery, useGetUserProfileQuery,useUtmParamsMutation } from './modules/authentication/slice/createAuthApiSlice';
import { setCredentials, setAllRolesPermissions, setRolePerCompany } from './modules/authentication/slice/authSlice';
import { useEffect } from 'react';
import { apiStatuses } from './shared/utils/constantData';
import { Warning } from './shared/components/Warning/Warning';
import { setCompanyData } from './modules/createJob/slice/companySlice';
import { getImageUrl } from './shared/utils/functions/images';
import FacebookPixel from './shared/utils/tracking/FacebookPixel'; // ENABLE ON PROD ONLY
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
declare global {
  interface Window {
    pagesense?: Array<Array<string | unknown>>;
    trackAhmedEssamFunnel?: (step: string, data?: any) => void;
  }
}

function App() {
  const location = useLocation();

  // Define funnel steps constants
  const AHMED_ESSAM_FUNNEL = 'ahmed_essam_funnel';
  const FUNNEL_STEPS = {
    VISIT: 'visit',
    AUTH: 'auth',
    SIGNUP: 'signup',
    PROFILE: 'profile',
    CONVERSION: 'conversion'
  };
  const [utmParams] = useUtmParamsMutation();
  // Initialize GA once
  useEffect(() => {
    ReactGA.initialize('G-4HEXQDC9BH');
    console.log('GA initialized');
  }, []);

  // Track page views
  useEffect(() => {
    if (location.pathname) {
      ReactGA.send({
        hitType: "pageview",
        page: location?.pathname + location?.search
      });
    }
  }, [location]);

  // UTM tracking and funnel entry
  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);

    const utm_source = searchParams.get('utm_source');
    const utm_medium = searchParams.get('utm_medium');
    const utm_campaign = searchParams.get('utm_campaign');
    const utm_content = searchParams.get('utm_content');

    // Only proceed if we have UTM parameters
    if (utm_source || utm_medium || utm_campaign || utm_content) {
      // Track general UTM visit
      ReactGA.event('utm_visit', {
        utm_source: utm_source || 'not_set',
        utm_medium: utm_medium || 'not_set',
        utm_campaign: utm_campaign || 'not_set',
        utm_content: utm_content || 'not_set',
      });

      console.log('UTM visit tracked:', { utm_source, utm_medium, utm_campaign, utm_content });

      // Special tracking for ahmed_essam funnel
      if (utm_content === 'ahmed_essam') {
        // Track funnel entry event
        ReactGA.event(`${AHMED_ESSAM_FUNNEL}_${FUNNEL_STEPS.VISIT}`, {
          utm_source,
          utm_medium,
          utm_campaign,
          utm_content,
          timestamp: new Date().toISOString()
        });

        console.log('Ahmed Essam funnel entry tracked');

        // Store funnel data in localStorage for cross-session tracking
        localStorage.setItem(AHMED_ESSAM_FUNNEL, JSON.stringify({
          entry_time: new Date().toISOString(),
          utm_source,
          utm_medium,
          utm_campaign,
          utm_content
        }));
      }
    }
  }, [FUNNEL_STEPS.VISIT, location?.search]);

  const [cookies, setCookie] = useCookies(['isActive'])
  const { isActive } = cookies
  const dispatch = useDispatch();
  const { data: user, isLoading, isSuccess, isError, error } = useGetUserProfileQuery();
  const { data, isSuccess: isPermissionsSuccess, isError: hasPermissionsError } = useGetPermissionsQuery(undefined, { skip: (isError && !isActive) || isLoading });
  
  // Track user authentication in funnel
  useEffect(() => {
    if (isSuccess && user) {
      const funnelData = localStorage.getItem(AHMED_ESSAM_FUNNEL);

      if (funnelData) {
        const parsedFunnelData = JSON.parse(funnelData);

        // Track authentication step in funnel
        ReactGA.event(`${AHMED_ESSAM_FUNNEL}_${FUNNEL_STEPS.AUTH}`, {
          user_id: user.id,
          user_email: user.email,
          time_to_auth: new Date().getTime() - new Date(parsedFunnelData.entry_time).getTime(),
          utm_source: parsedFunnelData.utm_source,
          utm_medium: parsedFunnelData.utm_medium,
          utm_campaign: parsedFunnelData.utm_campaign,
          utm_content: parsedFunnelData.utm_content
        });

        console.log('Ahmed Essam funnel authentication tracked');

        // Update funnel data with user info
        localStorage.setItem(AHMED_ESSAM_FUNNEL, JSON.stringify({
          ...parsedFunnelData,
          user_id: user.id,
          user_email: user.email,
          auth_time: new Date().toISOString()
        }));
      }
    }
  }, [FUNNEL_STEPS.AUTH, isSuccess, user]);

  // Helper function to track funnel progress (exposed globally for other components)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackFunnelStep = (step: any, additionalData = {}) => {
    const funnelData = localStorage.getItem(AHMED_ESSAM_FUNNEL);

    if (funnelData) {
      const parsedFunnelData = JSON.parse(funnelData);

      // Track step in funnel
      ReactGA.event(`${AHMED_ESSAM_FUNNEL}_${step}`, {
        step,
        timestamp: new Date().toISOString(),
        time_since_entry: new Date().getTime() - new Date(parsedFunnelData.entry_time).getTime(),
        user_id: user?.id || 'anonymous',
        ...additionalData,
        utm_source: parsedFunnelData.utm_source,
        utm_medium: parsedFunnelData.utm_medium,
        utm_campaign: parsedFunnelData.utm_campaign,
        utm_content: parsedFunnelData.utm_content
      });

      console.log(`Ahmed Essam funnel step tracked: ${step}`);

      // Update funnel data
      localStorage.setItem(AHMED_ESSAM_FUNNEL, JSON.stringify({
        ...parsedFunnelData,
        [`${step}_time`]: new Date().toISOString(),
        ...additionalData
      }));

      return true;
    }

    return false;
  };

  // Expose tracking function globally
  useEffect(() => {
    window.trackAhmedEssamFunnel = trackFunnelStep;

    // Return cleanup function
    return () => {
      delete window.trackAhmedEssamFunnel;
    };
  }, [trackFunnelStep, user]);

  // Zoho PageSense implementation
  useEffect(() => {
    console.log = function () { };
    if (!user) {
      return
    }
    const script = document.createElement('script');
    // script.src = "https://cdn.pagesense.io/js/nowpay/a47dc19c08a34259bca04370b4104a4a.js"; // Dev
    script.src = "https://cdn.pagesense.io/js/nowpay/42a6d75f0f28471aa9224ded3fa3bbba.js"; // Prod
    script.async = true;
    document.head.appendChild(script);
    const identifyUser = `${user?.email} ${user?.id}`

    // Call Zoho PageSense identifyUser function
    window.pagesense = window.pagesense || [];
    window.pagesense.push(['identifyUser', identifyUser ?? 'Unauthorized user']);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [user]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setCredentials({ user }))
      dispatch(setCompanyData(user?.company))
      setCookie('isActive', true, { path: '/' })
    }

    if (error?.data?.statusCode === apiStatuses.unAuthorized) {
      dispatch(setCredentials({ user: null }))
      setCookie('isActive', false, { path: '/' })
    }
  }, [isSuccess, isError, user, dispatch, error, setCookie])

  useEffect(() => {
    if (isPermissionsSuccess) dispatch(setAllRolesPermissions(data?.rolesPermissions))
    dispatch(setRolePerCompany(user?.adminRole ?? null));
    // eslint-disable-next-line
  }, [dispatch, isPermissionsSuccess, data, user])

    
  useEffect(() => {
    const URL = window.location.href;
    const searchParams = new URLSearchParams(location?.search);
    const params = Object.fromEntries(searchParams?.entries());

    // Check if any query parameter starts with 'utm_'
    const hasUtm = Array.from(searchParams?.keys())?.some(key => key?.startsWith('utm_'));
    if (!hasUtm) return;

    const bodyData = {
      link: URL,
      metadata: params,
    };

    (async () => {
      try {
        console.log('Tracking UTM parameters:', bodyData);
        await utmParams(bodyData);
      } catch (_) {
        // Optional: silently fail
      }
    })();
  }, [location?.search, utmParams]);

  return (
    <>
      <ConfigProvider theme={{ token: antdThemeTokenConfig }}>
        <FacebookPixel /> {/*ENABLE ON PROD ONLY*/}

        {(error?.data?.statusCode !== apiStatuses.unAuthorized && isError) || hasPermissionsError ?
          <div className='wrapper'>
            <div className='mt-12'>
              <Warning icon={<img alt="Warning!" src={getImageUrl('warningGray')} />} title='Something went wrong!' message='Please refresh to try again' btnText='Refresh' />
            </div>
          </div>
          : isSuccess || error?.data?.statusCode === apiStatuses.unAuthorized ?
            <>
              <div className={`relative min-h-screen App`}>
                {routes}
              </div>
            </> : null}
      </ConfigProvider>
    </>
  );
}

export default App;